/**
 * Phase 7 chunk 2B — locked constants for the sky-view world-space
 * horizon labels (N/E/S/W billboards 1 km out at the observer's local
 * horizon) + the zenith reticle (faint accent-color point directly
 * above). Cesium-free so Vitest can sanity-check the locked values.
 *
 * Spec-locked from docs/phase7.md § II decision 14 + chunk-2 description.
 */

/** Distance from the observer to each N/E/S/W label, in meters. Per
 *  phase7.md "anchored at the observer's location offset by 1 km in
 *  each cardinal direction at elevation 0." */
export const HORIZON_LABEL_OFFSET_M = 1000;

/** Altitude of the zenith reticle above the observer, in meters.
 *  Cesium PointPrimitive `pixelSize` keeps the reticle screen-stable
 *  regardless of distance — 5 km is far enough to not visually
 *  overlap with anything else, close enough that lookup math stays
 *  numerically clean. */
export const ZENITH_RETICLE_ALTITUDE_M = 5000;

/** Pixel size of the zenith reticle's outlined point. */
export const ZENITH_RETICLE_PIXEL_SIZE = 10;

/** Outline width of the zenith reticle (in pixels). Faint ring rather
 *  than a filled dot — accent color outline, transparent fill. */
export const ZENITH_RETICLE_OUTLINE_PX = 2;

/** Font for the N/E/S/W horizon labels. Mono to match the family
 *  aesthetic; size readable from 1 km distance at the locked FOV. */
export const HORIZON_LABEL_FONT = '20px monospace';

/** Cardinal directions in the order the SkyHorizonLayer renders them.
 *  Order matters only for stable test snapshots; the entities themselves
 *  don't care. */
export const CARDINAL_DIRECTIONS = ['N', 'E', 'S', 'W'] as const;

export type CardinalDirection = (typeof CARDINAL_DIRECTIONS)[number];

/** Bearing in degrees clockwise from north for each cardinal label. */
export const CARDINAL_BEARING_DEG: Record<CardinalDirection, number> = {
  N: 0,
  E: 90,
  S: 180,
  W: 270,
};

/** ENU-frame offset (in meters) for each cardinal label, expressed as
 *  (east, north) given the local east-north-up frame at the observer.
 *  Up component is always 0 (labels sit on the local horizon).
 *  Computed from the locked bearing + offset so the table stays in
 *  sync with HORIZON_LABEL_OFFSET_M without manual edits. */
export function cardinalEnuOffset(
  dir: CardinalDirection,
  distanceMeters: number = HORIZON_LABEL_OFFSET_M,
): { east: number; north: number; up: number } {
  const bearingRad = (CARDINAL_BEARING_DEG[dir] * Math.PI) / 180;
  return {
    east: Math.sin(bearingRad) * distanceMeters,
    north: Math.cos(bearingRad) * distanceMeters,
    up: 0,
  };
}

// ─── Chunk 2D: ground-level horizon ring ─────────────────────────────

/** Radius of the horizon ring drawn at the observer's ground level
 *  (meters). 1 km matches the N/E/S/W label distance so the ring sits
 *  exactly at the labels — making it read as a single "horizon line"
 *  at the user's local ground plane. */
export const HORIZON_RING_RADIUS_M = 1000;

/** Number of polyline segments approximating the ring. 64 is plenty
 *  to look smooth at any zoom level without overpaying on draw cost. */
export const HORIZON_RING_SEGMENTS = 64;

/** Altitude offset (meters above the observer's ground level) at which
 *  the ring sits. 1 m avoids z-fighting with the globe imagery
 *  underneath. */
export const HORIZON_RING_ALTITUDE_OFFSET_M = 1;

/** Polyline width in pixels. Thin enough to feel like a horizon line,
 *  thick enough to stay visible at long camera distances. */
export const HORIZON_RING_WIDTH_PX = 2;

/** Generate the ENU-frame points of the horizon ring at the observer.
 *  Returns an array of (east, north, up) tuples — one per segment plus
 *  a closing duplicate so the polyline visually closes cleanly.
 *  Pure math; tested directly in Vitest. */
export function horizonRingEnuPoints(
  radiusMeters: number = HORIZON_RING_RADIUS_M,
  segments: number = HORIZON_RING_SEGMENTS,
  altitudeOffsetMeters: number = HORIZON_RING_ALTITUDE_OFFSET_M,
): { east: number; north: number; up: number }[] {
  const out: { east: number; north: number; up: number }[] = [];
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * 2 * Math.PI;
    out.push({
      east: Math.sin(angle) * radiusMeters,
      north: Math.cos(angle) * radiusMeters,
      up: altitudeOffsetMeters,
    });
  }
  return out;
}
